import { useEffect } from "react";

import { slugify, track } from "@/lib/analytics";

/**
 * Three delegated listeners on the document: `cta_click`, `nav_click` and
 * `faq_expand`. One place, no per-component wiring.
 *
 * THE DATA-ATTRIBUTE CONTRACT (this is what the copy pass writes):
 *
 *   data-cta-id="hero-primary"        → cta_click {id, position, page}
 *   data-cta-position="hero"          → the `position` on that event (default
 *                                       "unknown"): hero | mid | footer | nav
 *   data-nav="pricing"                → nav_click {item}; any control inside a
 *                                       <nav> also reports, using its own text
 *   data-faq-id="what-does-it-cost"   → faq_expand {id} when a <details> opens
 *                                       (falls back to the <summary>'s text)
 *
 * ONE CTA IS COUNTED ONCE. A component may EITHER carry `data-cta-id` OR call
 * `track("cta_click", …)` itself — never both, or every click on it is two
 * clicks and the funnel's step 2 doubles. The attribute is the default; the
 * explicit call is for a control whose id depends on runtime state.
 *
 * Capture phase, so a component that calls `stopPropagation()` in its own
 * handler does not silently remove itself from the measurement. Every value
 * sent is `slugify()`d — bounded, lowercase, 48 characters — and every one of
 * them comes from our own markup or our own copy. A visitor cannot put text
 * into a nav link.
 *
 * `toggle` does not bubble, which is why the FAQ listener is registered in the
 * capture phase as well: a non-bubbling event still travels DOWN through every
 * ancestor before it reaches its target.
 */
export function useUiClicks(page: string): void {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const cta = target.closest("[data-cta-id]");
      if (cta) {
        track("cta_click", {
          id: slugify(cta.getAttribute("data-cta-id") ?? ""),
          position: slugify(cta.getAttribute("data-cta-position") ?? "unknown"),
          page,
        });
      }

      // Either an explicitly marked item, or anything clickable inside a <nav>.
      // The navbar, the footer's link columns, and the breadcrumbs are all
      // <nav>s already, so this measures them with no markup change at all.
      const marked = target.closest("[data-nav]");
      const control = target.closest("a, button");
      if (marked || (control && control.closest("nav"))) {
        const label =
          marked?.getAttribute("data-nav") ||
          control?.getAttribute("aria-label") ||
          control?.textContent ||
          "";
        track("nav_click", { item: slugify(label) });
      }
    };

    const onToggle = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.tagName.toLowerCase() !== "details") return;
      if (!(target as HTMLDetailsElement).open) return; // closing is not an expand
      const summary = target.querySelector("summary");
      const id =
        target.getAttribute("data-faq-id") || (summary?.textContent ?? "");
      track("faq_expand", { id: slugify(id) });
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("toggle", onToggle, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("toggle", onToggle, true);
    };
  }, [page]);
}
